import React, { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import { articles } from '../../data/articles';
import RelatedArticlesCarousel from './components/RelatedArticlesCarousel';
import { handlePhoneClick, trackPhoneCall } from '../../utils/trackPhoneCall';
import { siteConfig } from '../../config/site';

const Article = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find(a => a.slug === slug);
  const articleContentRef = useRef<HTMLDivElement>(null);

  // Adiciona event listener para rastrear cliques em links de telefone dentro do conteúdo HTML
  useEffect(() => {
    const contentElement = articleContentRef.current;
    if (!contentElement) return;

    const handlePhoneLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const phoneLink = target.closest('a[href^="tel:"]');
      
      if (phoneLink) {
        e.preventDefault();
        trackPhoneCall();
        // Permite que o link continue funcionando normalmente
        const href = (phoneLink as HTMLAnchorElement).href;
        if (href) {
          window.location.href = href;
        }
      }
    };

    contentElement.addEventListener('click', handlePhoneLinkClick);

    return () => {
      contentElement.removeEventListener('click', handlePhoneLinkClick);
    };
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-24 text-center py-20">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Artigo não encontrado</h1>
          <Link to="/blog" className="text-blue-600 hover:text-blue-700">
            Voltar ao blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-PT', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <article className="pt-20">
        {/* Hero Image */}
        <div className="relative h-48 md:h-64 overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm text-gray-600 mb-4">
            <span className="bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full font-medium">
              {article.category}
            </span>
            <span>{formatDate(article.date)}</span>
            <span>•</span>
            <span>{article.readTime} min de leitura</span>
            <span>•</span>
            <span>Por {article.author}</span>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {article.title}
          </h1>

          {/* Excerpt */}
          <p className="text-lg text-gray-600 mb-6 leading-relaxed">
            {article.excerpt}
          </p>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-8">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-2.5 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>

          {/* Article Body */}
          <div 
            ref={articleContentRef}
            className="prose prose-base max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4 prose-p:text-base prose-ul:text-gray-700 prose-li:text-gray-700 prose-a:text-blue-600 prose-strong:text-gray-900 prose-a:underline hover:prose-a:text-blue-700 prose-h2:text-xl prose-h2:font-bold prose-h2:mt-6 prose-h2:mb-3 prose-h3:text-lg prose-h3:font-bold prose-h3:mt-4 prose-h3:mb-2 prose-ul:list-disc prose-ul:pl-5 prose-ul:mb-4 prose-li:mb-1.5 prose-li:text-base prose-ol:list-decimal prose-ol:pl-5 prose-ol:mb-4 prose-ol:text-base prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-600 prose-code:text-sm prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:overflow-x-auto"
            style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Link para serviço relacionado */}
          {article.relatedServiceId && (
            <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Precisa deste serviço?
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                A {siteConfig.name} oferece este serviço com profissionais experientes e equipamento de última geração.
              </p>
              <Link
                to={`/servico/${article.relatedServiceId}`}
                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold text-sm"
              >
                <span>Ver detalhes do serviço</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          )}

          {/* CTA */}
          <div className="mt-8 p-5 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg text-white">
            <h3 className="text-lg font-bold mb-2">
              Precisa de ajuda com canalização?
            </h3>
            <p className="text-blue-100 text-sm mb-4">
              Contacte a {siteConfig.name} para um serviço profissional e rápido.
            </p>
            <div className="flex flex-wrap gap-2">
              <a
                href={`tel:${siteConfig.phone}`}
                onClick={handlePhoneClick}
                className="bg-white text-blue-600 hover:bg-gray-100 font-semibold py-2 px-4 rounded-lg transition-colors inline-flex items-center gap-2 text-sm"
              >
                <i className="ri-phone-line"></i>
                Ligar: {siteConfig.phoneDisplay}
              </a>
              <a
                href={`tel:${siteConfig.phone}`}
                onClick={handlePhoneClick}
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold py-2 px-4 rounded-lg transition-colors inline-flex items-center gap-2 text-sm"
              >
                <i className="ri-phone-line"></i>
                Pedir Orçamento
              </a>
              <Link
                to="/blog"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold py-2 px-4 rounded-lg transition-colors inline-flex items-center gap-2 text-sm"
              >
                <i className="ri-article-line"></i>
                Ver mais artigos
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Carrossel de Artigos Relacionados */}
      <RelatedArticlesCarousel currentArticleId={article.id} />

      <Footer />
    </div>
  );
};

export default Article;

